import { Box, Button, Flex, Group, NumberInput, Stack, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CarForm } from "../interfaces/CreateCarForm.types";

import { Dropdown, FormField } from "@/components/elements";

import type { ElementType, FC } from "react";

interface Props {
  WrapperForFields: ElementType;
  wrapperProps: Record<string, unknown>;
  form: CarForm;
  showLessElements: boolean;
  activeStep: number;
  minProductionYear: number;
  maxProductionYear: number;
  onCancel: (() => void) | undefined;
  loading: boolean;
  nextStep: () => void;
  isStepValid: (step: number) => boolean;
  familyOptions: {
    value: string;
    label: string;
  }[];
  isLoadingFamilies: boolean;
  handleFamilyChange: (value?: string | null | undefined) => void;
  generationOptions: {
    value: string;
    label: string;
  }[];
  isLoadingGenerations: boolean;
  handleGenerationChange: (value?: string | null | undefined) => void;
  modelOptions: {
    value: string;
    label: string;
  }[];
  isLoadingModels: boolean;
  handleModelChange: (value?: string | null | undefined) => void;
  variantOptions: {
    value: string;
    label: string;
  }[];
  isLoadingVariants: boolean;
  handleVariantChange: (value?: string | null | undefined) => void;
}

const Step2: FC<Props> = ({
  WrapperForFields,
  wrapperProps,
  form,
  showLessElements,
  activeStep,
  onCancel,
  maxProductionYear,
  minProductionYear,
  loading,
  nextStep,
  isStepValid,
  familyOptions,
  isLoadingFamilies,
  handleFamilyChange,
  generationOptions,
  isLoadingGenerations,
  handleGenerationChange,
  modelOptions,
  isLoadingModels,
  handleModelChange,
  variantOptions,
  isLoadingVariants,
  handleVariantChange,
}) => {
  const { t } = useTranslation();

  return (
    <Stack gap="md">
      <Title order={4} mb="sm">
        {t("components.CreateCarForm.titles.vehicleDetails")}
      </Title>
      <WrapperForFields {...wrapperProps}>
        <FormField required>
          <Dropdown
            label={t("car.family")}
            placeholder={t("components.CreateCarForm.placeholders.family")}
            data={familyOptions}
            searchable
            showLessElements={showLessElements}
            disabled={isLoadingFamilies || !form.values.make}
            onChange={handleFamilyChange}
            value={form.values.family}
          />
        </FormField>
        <FormField required>
          <Dropdown
            label={t("car.generation")}
            placeholder={t("components.CreateCarForm.placeholders.generation")}
            data={generationOptions}
            searchable
            showLessElements={showLessElements}
            disabled={isLoadingGenerations || !form.values.make || !form.values.family}
            onChange={handleGenerationChange}
            value={form.values.generation}
          />
        </FormField>
      </WrapperForFields>
      <WrapperForFields {...wrapperProps}>
        <FormField required>
          <Dropdown
            label={t("car.model")}
            placeholder={t("components.CreateCarForm.placeholders.model")}
            data={modelOptions}
            searchable
            showLessElements={showLessElements}
            disabled={
              isLoadingModels || !form.values.make || !form.values.family || !form.values.generation
            }
            onChange={handleModelChange}
            value={form.values.model}
          />
        </FormField>
        <FormField required>
          <Dropdown
            label={t("car.variant")}
            placeholder={t("components.CreateCarForm.placeholders.variant")}
            data={variantOptions}
            searchable
            showLessElements={showLessElements}
            disabled={
              isLoadingVariants ||
              !form.values.make ||
              !form.values.family ||
              !form.values.generation ||
              !form.values.model
            }
            onChange={handleVariantChange}
            value={form.values.variant}
          />
        </FormField>
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <FormField required>
          <NumberInput
            label={t("car.production_year")}
            placeholder={t("components.CreateCarForm.placeholders.production_year")}
            min={minProductionYear}
            max={maxProductionYear}
            error={t("common.required_field")}
            disabled={
              !form.values.make
              /* !form.values.family ||
                      !form.values.generation ||
                      !form.values.model ||
                      !form.values.variant */
            }
            {...form.getInputProps("production_year")}
          />
        </FormField>

        <Box />
      </WrapperForFields>

      <Group justify="space-between" mt="2em">
        <Text c="var(--mantine-color-primaryDark-5)" fw={500} fz="sm">
          Step {activeStep + 1} of 4
        </Text>

        <Flex justify="end" gap="md">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("common.cancel")}
              c="dark"
              miw={100}
            >
              {t("common.cancel")}
            </Button>
          )}
          <Button
            type="button"
            onClick={nextStep}
            disabled={!isStepValid(1)}
            aria-label={t("common.next")}
            miw={100}
          >
            {t("common.next")}
          </Button>
        </Flex>
      </Group>
    </Stack>
  );
};
export default Step2;
